// Unsplash real photos — all CC0 / free to use
export const SERVICES = [
  {
    id: "brand",
    num: "01",
    title: "Brand",
    tagline: "Everything that defines how the business is seen.",
    hero: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Brand Strategy",
      "Brand Positioning",
      "Brand Identity",
      "Logo Design",
      "Visual Identity",
      "Brand Guidelines",
      "Rebranding",
      "Packaging Design",
      "Marketing Collateral",
    ],
    detail: {
      headline: "A brand isn't a logo. It's what people feel when they see your name.",
      body: "We build brand systems from the ground up — starting with strategy and positioning, then translating that into a visual identity that works everywhere. From your first logo to a full rebrand, we make sure your business looks as good as it performs.",
      what: [
        { title: "Brand Strategy", desc: "Clarity on who you are, who you're for, and how you're different. The foundation everything else is built on." },
        { title: "Brand Positioning", desc: "Define your space in the market. What you stand for, what you don't, and why customers should choose you." },
        { title: "Brand Identity", desc: "The complete visual and verbal system — logo, colour, typography, tone of voice and usage rules." },
        { title: "Logo Design", desc: "Considered, flexible logos designed to work at every size and in every context." },
        { title: "Visual Identity", desc: "Colours, type, imagery style and graphic elements that make your brand instantly recognisable." },
        { title: "Brand Guidelines", desc: "A clear rulebook so your brand stays consistent wherever it shows up." },
        { title: "Rebranding", desc: "Evolve an existing brand without losing what makes it yours. Strategic refresh with a clear rationale." },
        { title: "Packaging Design", desc: "Packaging that sells on shelf and ships beautifully. Form and function in balance." },
        { title: "Marketing Collateral", desc: "Business cards, brochures, presentations — everything that represents the brand offline." },
      ],
      image: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=900&q=80",
    },
  },
  {
    id: "creative",
    num: "02",
    title: "Creative & Design",
    tagline: "Everything needed to communicate the brand visually.",
    hero: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Graphic Design",
      "Social Media Creatives",
      "Ad Creatives",
      "Campaign Creatives",
      "Posters",
      "Brochures",
      "Flyers",
      "Catalogues",
      "Presentation Design",
      "Product Creative Design",
      "Motion Graphics",
      "Creative Direction",
    ],
    detail: {
      headline: "Design that communicates before a single word is read.",
      body: "Great creative isn't decoration — it's communication. We design everything your brand needs to show up clearly and consistently across every surface, from a single social post to a full campaign rollout.",
      what: [
        { title: "Graphic Design", desc: "Clean, intentional design across all formats. Every pixel has a purpose." },
        { title: "Social Media Creatives", desc: "Scroll-stopping visuals built for each platform's format and audience." },
        { title: "Ad Creatives", desc: "High-performance ad visuals for Meta, Google and beyond — designed to convert." },
        { title: "Campaign Creatives", desc: "Cohesive visual systems for product launches, seasonal campaigns and promotions." },
        { title: "Posters & Print", desc: "Event posters, promotions and display materials built for physical environments." },
        { title: "Brochures & Flyers", desc: "Sales-ready print materials that tell the brand story clearly." },
        { title: "Catalogues", desc: "Product catalogues that present your range with clarity and visual confidence." },
        { title: "Presentation Design", desc: "Pitch decks and investor presentations designed to win the room." },
        { title: "Motion Graphics", desc: "Animated visuals for social, ads and video — brand in motion." },
        { title: "Creative Direction", desc: "The strategic eye that ensures all creative output stays cohesive and on-brand." },
      ],
      image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?w=900&q=80",
    },
  },
  {
    id: "website",
    num: "03",
    title: "Website & Digital",
    tagline: "Build the digital foundation of the business.",
    hero: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Landing Pages",
      "Business Websites",
      "Corporate Websites",
      "Portfolio Websites",
      "Custom Websites",
      "Advanced Web Applications",
      "UI/UX Design",
      "Website Redesign",
      "Website Maintenance",
      "Domain & Hosting Setup",
      "Conversion-focused Landing Pages",
    ],
    detail: {
      headline: "Your website is your most important salesperson. Make it work.",
      body: "We build websites that are fast, beautiful and built to convert. From a single landing page to a complex custom web application — every project starts with user experience and ends with measurable performance.",
      what: [
        { title: "Landing Pages", desc: "Single-focus pages built to capture leads, drive sign-ups or sell a product." },
        { title: "Business Websites", desc: "Professional multi-page sites that represent the brand and generate enquiries." },
        { title: "Corporate Websites", desc: "Polished, large-scale websites for established businesses and enterprises." },
        { title: "Portfolio Websites", desc: "Beautifully presented work showcases for creatives, agencies and professionals." },
        { title: "Custom Websites", desc: "Bespoke builds designed and developed entirely around your specific requirements." },
        { title: "Advanced Web Applications", desc: "Complex, feature-rich platforms — booking systems, dashboards, portals and more." },
        { title: "UI/UX Design", desc: "User flows, wireframes and interface design that puts the user experience first." },
        { title: "Website Redesign", desc: "Transform an outdated site into a modern, high-performing digital presence." },
        { title: "Website Maintenance", desc: "Ongoing updates, security, backups and performance monitoring." },
        { title: "Domain & Hosting Setup", desc: "End-to-end setup of your domain, hosting environment and SSL." },
      ],
      image: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=900&q=80",
    },
  },
  {
    id: "ecommerce",
    num: "04",
    title: "E-Commerce",
    tagline: "Turn products into a digital storefront.",
    hero: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "E-commerce Website Development",
      "Online Store Setup",
      "Product Catalogue Setup",
      "Payment Gateway Integration",
      "Shipping Integration",
      "Order Management Setup",
      "Shopify Development",
      "WooCommerce Development",
      "E-commerce UI/UX",
      "Store Optimisation",
      "E-commerce Maintenance",
    ],
    detail: {
      headline: "A store that sells — not just a catalogue that exists online.",
      body: "We build e-commerce experiences that are designed to sell. From a first Shopify store to a fully custom online retail platform — we handle everything from setup to optimisation, so your products reach customers and revenue follows.",
      what: [
        { title: "E-commerce Development", desc: "Full-stack store builds on Shopify, WooCommerce or custom platforms." },
        { title: "Online Store Setup", desc: "Complete store configuration — products, collections, pricing and policies." },
        { title: "Product Catalogue Setup", desc: "Clean, well-structured product listings that present your range clearly." },
        { title: "Payment Gateway Integration", desc: "Razorpay, Stripe, PayPal and other gateways — configured and tested." },
        { title: "Shipping Integration", desc: "Real-time shipping rates, fulfilment partner integrations and tracking." },
        { title: "Shopify Development", desc: "Custom themes, apps and functionality built on Shopify's platform." },
        { title: "WooCommerce Development", desc: "Flexible, scalable stores on WordPress / WooCommerce." },
        { title: "E-commerce UI/UX", desc: "Conversion-led design — from product pages to checkout flow optimisation." },
        { title: "Store Optimisation", desc: "Speed, SEO and conversion improvements on existing stores." },
      ],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80",
    },
  },
  {
    id: "content",
    num: "05",
    title: "Content Creation",
    tagline: "Content built around the brand and audience.",
    hero: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Social Media Content",
      "Reels",
      "Short-form Videos",
      "Product Photography",
      "Product Videos",
      "Lifestyle Content",
      "Brand Films",
      "Promotional Videos",
      "Educational Content",
      "Founder Content",
      "Interview Content",
      "Content Calendars",
      "Creative Concepts",
      "Script Writing",
      "Copywriting",
    ],
    detail: {
      headline: "Content that earns attention — not just fills a feed.",
      body: "We create content that works for your brand and your audience. From a product Reel to a brand film, from a content calendar to a full copywriting system — we build content with a clear purpose and a consistent voice.",
      what: [
        { title: "Social Media Content", desc: "Platform-native content designed to perform on Instagram, Facebook, LinkedIn and more." },
        { title: "Reels & Short-form Video", desc: "Attention-grabbing short videos built for the algorithm and the audience." },
        { title: "Product Photography", desc: "Clean, professional product imagery for e-commerce, social and campaigns." },
        { title: "Brand Films", desc: "Cinematic brand stories that communicate what you stand for in under two minutes." },
        { title: "Founder Content", desc: "Personal brand content for founders — positioning you as the face of the business." },
        { title: "Content Calendars", desc: "Planned, structured monthly content strategy across all active platforms." },
        { title: "Script Writing", desc: "Crisp, clear scripts for ads, explainers, brand films and social video." },
        { title: "Copywriting", desc: "Website copy, campaign messaging, product descriptions and brand voice guidelines." },
      ],
      image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=900&q=80",
    },
  },
  {
    id: "production",
    num: "06",
    title: "Production & Shoots",
    tagline: "From concept to final frame.",
    hero: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Ad Shoots",
      "Advertisement Production",
      "Campaign Shoots",
      "Product Shoots",
      "Brand Shoots",
      "Commercial Shoots",
      "Fashion Shoots",
      "Jewellery Shoots",
      "Food Shoots",
      "Lifestyle Shoots",
      "Corporate Shoots",
      "Founder / Personal Brand Shoots",
      "Event Coverage",
      "Promotional Shoots",
      "Reel Shoots",
      "Video Production",
      "Photography Production",
      "Post-production",
      "Video Editing",
      "Color Grading",
      "Motion Graphics",
    ],
    detail: {
      headline: "We don't just shoot. We produce.",
      body: "Full-scale production from concept to final deliverable. We handle everything — pre-production planning, shoot day execution and post-production finishing. The result is content that looks expensive, because it's made properly.",
      what: [
        { title: "Ad & Campaign Shoots", desc: "Fully produced shoots for advertising campaigns across digital and print." },
        { title: "Product Shoots", desc: "Clean, conversion-focused product photography for e-commerce and marketing." },
        { title: "Brand & Commercial Shoots", desc: "High-production shoots that capture the brand in its truest form." },
        { title: "Fashion & Jewellery Shoots", desc: "Editorial-quality fashion and jewellery photography for D2C and retail brands." },
        { title: "Food & Lifestyle Shoots", desc: "Mouth-watering food photography and lifestyle imagery for consumer brands." },
        { title: "Founder / Personal Brand", desc: "Professional shoots that position founders as credible, confident faces of their brand." },
        { title: "Video Production", desc: "End-to-end video production — scripted, shot, edited and graded." },
        { title: "Post-production", desc: "Editing, colour grading, sound design and motion graphics finishing." },
      ],
      image: "https://images.unsplash.com/photo-1540655037529-dec987208707?w=900&q=80",
    },
  },
  {
    id: "social",
    num: "07",
    title: "Social Media",
    tagline: "Build and manage the brand's presence.",
    hero: "https://images.unsplash.com/photo-1611262588024-d12430b98920?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Social Media Strategy",
      "Social Media Management",
      "Instagram Management",
      "Facebook Management",
      "Content Planning",
      "Content Calendars",
      "Community Management",
      "Page Optimisation",
      "Social Media Campaigns",
      "Trend-based Content",
      "Monthly Content Strategy",
      "Social Media Analytics",
      "Performance Reporting",
    ],
    detail: {
      headline: "Consistent, strategic presence across every platform your audience uses.",
      body: "Social media that actually builds something — brand awareness, community, trust and customers. We manage your entire social presence or work alongside your team as a strategy and creative partner.",
      what: [
        { title: "Social Media Strategy", desc: "A clear plan — which platforms, what content, what tone, what goals." },
        { title: "Social Media Management", desc: "End-to-end management of your social presence — creation, posting and reporting." },
        { title: "Content Calendars", desc: "Structured monthly calendars with content planned in advance and approved on time." },
        { title: "Community Management", desc: "Responding to comments, DMs and building an engaged following." },
        { title: "Social Media Campaigns", desc: "Dedicated campaign pushes for launches, promotions and seasonal moments." },
        { title: "Performance Analytics", desc: "Monthly reporting on reach, engagement, growth and what's actually working." },
      ],
      image: "https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=900&q=80",
    },
  },
  {
    id: "performance",
    num: "08",
    title: "Performance Marketing",
    tagline: "Turn attention into measurable business outcomes.",
    hero: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Meta Ads",
      "Facebook Ads",
      "Instagram Ads",
      "Google Ads",
      "Search Ads",
      "Display Ads",
      "YouTube Ads",
      "Remarketing",
      "Retargeting",
      "Lead Generation Campaigns",
      "E-commerce Campaigns",
      "Campaign Strategy",
      "Audience Targeting",
      "Conversion Optimisation",
      "Ad Creative Strategy",
      "Performance Analytics",
    ],
    detail: {
      headline: "Paid marketing that pays for itself.",
      body: "We run performance campaigns that are built to deliver real results — leads, sales, enquiries. Strategy, creative, targeting and optimisation all handled by one team, so everything works together.",
      what: [
        { title: "Meta Ads", desc: "Facebook and Instagram advertising — awareness, lead generation and direct sales." },
        { title: "Google Ads", desc: "Search, display and YouTube campaigns that capture demand and drive conversions." },
        { title: "Remarketing & Retargeting", desc: "Re-engage warm audiences who've interacted with your brand but haven't converted yet." },
        { title: "Lead Generation", desc: "Campaigns built specifically to generate qualified enquiries and leads for your business." },
        { title: "E-commerce Campaigns", desc: "Product catalogue ads, dynamic ads and shopping campaigns for online stores." },
        { title: "Ad Creative Strategy", desc: "Creative concepts and visual assets designed to perform — not just look good." },
        { title: "Performance Analytics", desc: "Transparent reporting on spend, results and what we're doing to improve them." },
      ],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80",
    },
  },
  {
    id: "seo",
    num: "09",
    title: "SEO & Organic Growth",
    tagline: "Build long-term discoverability.",
    hero: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "SEO Strategy",
      "Technical SEO",
      "On-page SEO",
      "Off-page SEO",
      "Local SEO",
      "Google Business Profile Optimisation",
      "Keyword Research",
      "Content SEO",
      "Website SEO Audits",
      "SEO Optimisation",
      "Search Visibility Strategy",
    ],
    detail: {
      headline: "Show up when people are searching for exactly what you do.",
      body: "SEO that compounds. We build organic search visibility through technical foundations, smart content strategy and authority-building — so your business keeps ranking long after the work is done.",
      what: [
        { title: "SEO Strategy", desc: "A clear roadmap — what to fix, what to create, and what to build links toward." },
        { title: "Technical SEO", desc: "Site speed, crawlability, structured data and everything Google needs to rank you." },
        { title: "On-page SEO", desc: "Keyword targeting, content optimisation and meta data across every page." },
        { title: "Local SEO", desc: "Ranking in local search and Google Maps for businesses targeting a specific location." },
        { title: "Google Business Profile", desc: "Full GBP setup and optimisation to dominate local search results." },
        { title: "Content SEO", desc: "Articles, landing pages and content built to rank for the right search terms." },
        { title: "SEO Audits", desc: "A complete picture of where your site stands and exactly what needs to change." },
      ],
      image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=900&q=80",
    },
  },
  {
    id: "strategy",
    num: "10",
    title: "Marketing Strategy",
    tagline: "A marketing partner, not just an execution agency.",
    hero: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Digital Marketing Strategy",
      "Brand Strategy",
      "Campaign Strategy",
      "Go-to-Market Strategy",
      "Content Strategy",
      "Social Media Strategy",
      "Customer Journey Planning",
      "Audience Research",
      "Competitor Analysis",
      "Marketing Planning",
      "Growth Strategy",
    ],
    detail: {
      headline: "Strategy is the difference between spending money and making it.",
      body: "Before we execute anything, we think. We build marketing strategies that connect business objectives to the right channels, the right audiences and the right messages — so every rupee of marketing spend has a reason.",
      what: [
        { title: "Digital Marketing Strategy", desc: "An integrated plan across all digital channels — built around your business goals." },
        { title: "Go-to-Market Strategy", desc: "How to launch a product, brand or business with maximum impact and minimum waste." },
        { title: "Content Strategy", desc: "What to say, where to say it, how often — and why it matters to your audience." },
        { title: "Customer Journey Planning", desc: "Map the path from stranger to customer to advocate — and optimise every step." },
        { title: "Audience Research", desc: "Understand who your customer actually is, what they want, and where they spend time." },
        { title: "Competitor Analysis", desc: "Know your market — who's winning, who's not, and where the opportunity sits." },
        { title: "Growth Strategy", desc: "A long-term plan for scaling — combining brand, digital and performance into one system." },
      ],
      image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=900&q=80",
    },
  },
  {
    id: "automation",
    num: "11",
    title: "Automation & Digital Systems",
    tagline: "Make growth more efficient.",
    hero: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Marketing Automation",
      "Lead Automation",
      "WhatsApp Automation",
      "Email Automation",
      "CRM Setup",
      "Lead Management Systems",
      "Customer Follow-up Systems",
      "Workflow Automation",
      "Form & Lead Integrations",
      "Reporting Automation",
      "Business Process Automation",
      "Appointment / Enquiry Automation",
    ],
    detail: {
      headline: "Systems that work while you sleep.",
      body: "We build the digital infrastructure that removes manual effort from your marketing and sales. From a WhatsApp follow-up sequence to a full CRM and lead management system — automation that actually serves the business.",
      what: [
        { title: "Marketing Automation", desc: "Automated email, SMS and WhatsApp sequences that nurture leads and retain customers." },
        { title: "Lead Automation", desc: "Capture, qualify and route leads automatically — no manual effort required." },
        { title: "WhatsApp Automation", desc: "Automated WhatsApp messaging for enquiries, follow-ups and customer communication." },
        { title: "CRM Setup", desc: "Configure and customise a CRM that fits how your team actually works." },
        { title: "Lead Management Systems", desc: "End-to-end systems for capturing, tracking and converting leads." },
        { title: "Workflow Automation", desc: "Remove repetitive tasks from your team's day with smart workflow automation." },
        { title: "Reporting Automation", desc: "Automated dashboards and reports that give you the numbers without the manual work." },
      ],
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=900&q=80",
    },
  },
  {
    id: "technology",
    num: "12",
    title: "Technology & AI",
    tagline: "The technology layer of the business.",
    hero: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "AI-powered Business Solutions",
      "AI Content Workflows",
      "AI Automation",
      "AI Chatbots",
      "Customer Support Systems",
      "AI-assisted Marketing Systems",
      "Custom Digital Tools",
      "Business Dashboards",
      "Internal Digital Systems",
      "API / Third-party Integrations",
    ],
    detail: {
      headline: "AI and technology that give your business a real edge.",
      body: "We build intelligent tools, AI-assisted systems and custom technology solutions that make your business faster, smarter and more scalable. Not AI for the sake of it — AI that solves a real problem.",
      what: [
        { title: "AI-powered Solutions", desc: "Custom AI implementations built around specific business challenges and workflows." },
        { title: "AI Chatbots", desc: "Intelligent conversational interfaces for sales, support and lead qualification." },
        { title: "AI Content Workflows", desc: "AI-assisted systems that accelerate content creation without sacrificing quality." },
        { title: "Custom Digital Tools", desc: "Bespoke tools built for your team — calculators, dashboards, internal platforms." },
        { title: "Business Dashboards", desc: "Real-time data dashboards that give leadership a clear view of business performance." },
        { title: "API Integrations", desc: "Connect your tools, platforms and data sources into one cohesive system." },
      ],
      image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900&q=80",
    },
  },
  {
    id: "campaigns",
    num: "13",
    title: "Campaigns & Launches",
    tagline: "Integrated campaigns from concept to result.",
    hero: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=1400&q=80",
    accent: "#c9962e",
    items: [
      "Product Launch Campaigns",
      "Brand Launches",
      "Festival Campaigns",
      "Seasonal Campaigns",
      "Promotional Campaigns",
      "Digital Campaigns",
      "Integrated Campaigns",
      "Influencer Campaign Support",
      "Campaign Creative",
      "Campaign Production",
      "Campaign Management",
    ],
    detail: {
      headline: "When a moment matters, make it impossible to miss.",
      body: "Launches, festivals, promotions and seasonal moments deserve more than a single post. We build integrated campaigns that combine creative, production, paid media and organic — so the message lands everywhere at once.",
      what: [
        { title: "Product Launch Campaigns", desc: "End-to-end launch strategy and execution — from teaser to post-launch analysis." },
        { title: "Brand Launches", desc: "Introduce a new brand or sub-brand to the market with a coordinated rollout." },
        { title: "Festival & Seasonal Campaigns", desc: "Diwali, Eid, Christmas, New Year — relevant campaigns that capture the moment." },
        { title: "Integrated Campaigns", desc: "Creative, media, social and production working together as one campaign system." },
        { title: "Campaign Creative", desc: "The visual and verbal identity of the campaign — concept, design and messaging." },
        { title: "Campaign Production", desc: "Shoots, video production and content creation built around the campaign brief." },
        { title: "Campaign Management", desc: "Coordinating all moving parts — creative, media, posting schedule and reporting." },
      ],
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=80",
    },
  },
];
